import { FC, memo, useMemo } from 'react'

import { Form } from 'brief-form'
import { Dialog, DialogFace, DialogFooterActions, Hr, LeaveConfirmation } from 'ui/components'
import { ChoiceOption } from 'ui/types'

import { CreateUserDialogProps } from './CreateUserDialogProps'
import { AssignedRelationshipsForm, ClientUserGeneralInformationForm, PermissionsForm } from './components'
import { useCreateUserDialog } from './hooks'

export const CreateUserDialog: FC<CreateUserDialogProps> = memo((props) => {
  const { onClose, clientUserForEditing, permissionsStructure, relationshipsToAssign } = props
  const relationshipsToAssignOptions = useMemo(() => {
    const mergedOptionsMap = new Map<string, ChoiceOption>(
      relationshipsToAssign.map((option) => [String(option.value), option] as const)
    )

    for (const relationship of clientUserForEditing?.assignedRelationships || []) {
      const key = String(relationship.id)

      if (!mergedOptionsMap.has(key)) {
        mergedOptionsMap.set(key, { value: relationship.id, label: relationship.name })
      }
    }

    return [...mergedOptionsMap.values()]
  }, [clientUserForEditing?.assignedRelationships, relationshipsToAssign])

  const { isModeCreating, onCloseWrapper, onSubmitWrapper, isFormChanged, config, Field, isSubmittingClientUserForm } =
    useCreateUserDialog(clientUserForEditing, permissionsStructure, onClose)

  return (
    <LeaveConfirmation preventLeaving={isFormChanged && !isSubmittingClientUserForm}>
      <Dialog
        id="create-client-user-dialog"
        title={isModeCreating ? 'Add New Client User' : `${clientUserForEditing?.name}`}
        subTitle={!isModeCreating ? 'Edit Relationships & Permissions' : null}
        face={DialogFace.PRIMARY}
        onClose={onCloseWrapper}
        footerActions={[DialogFooterActions.SAVE, DialogFooterActions.CANCEL]}
        onSubmit={onSubmitWrapper}
        isSubmitting={isSubmittingClientUserForm}
      >
        <Form config={config}>
          {isModeCreating && <ClientUserGeneralInformationForm Field={Field} />}
          <AssignedRelationshipsForm
            relationshipsToAssign={relationshipsToAssignOptions}
            Field={Field}
            isModeCreating={isModeCreating}
          />
          <Hr />
          <PermissionsForm permissions={permissionsStructure} formValue={config.value} Field={Field} />
        </Form>
      </Dialog>
    </LeaveConfirmation>
  )
})
